/**
 * Übersicht (Spec 8.1).
 *
 * Oben der Arbeitsfluss des laufenden Monats mit genau einem nächsten Schritt,
 * darunter die Arbeit, die dieser Schritt meint. Wer die Software zum ersten
 * Mal öffnet, soll ohne Anleitung wissen, was als Nächstes zu tun ist.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  api,
  type BusinessDaySummary,
  type CashSessionDto,
  type WorkflowDto,
} from "../lib/api";
import { euro, longDate } from "../lib/format";
import { Workflow } from "../components/Workflow";
import { useMe } from "./Anmeldung";
import { canWrite } from "../lib/auth";
import {
  Amount,
  Empty,
  ErrorBox,
  FlagChip,
  PageHeader,
  Section,
  Spinner,
  Stat,
  StatusChip,
} from "../components/ui";

export function Dashboard() {
  const days = useQuery({
    queryKey: ["business-days"],
    queryFn: () => api.get<{ days: BusinessDaySummary[] }>("/business-days"),
  });
  const blocking = useQuery({
    queryKey: ["cash-sessions", "blocking"],
    queryFn: () => api.get<{ sessions: CashSessionDto[] }>("/cash-sessions?onlyBlocking=true"),
  });
  const workflow = useQuery({
    queryKey: ["workflow"],
    queryFn: () => api.get<WorkflowDto>("/workflow"),
  });
  // Wer Trinkgeld bekommt und keine IBAN hat, hält die Auszahlung auf. Nur für
  // die, die IBANs pflegen dürfen — alle anderen könnten ohnehin nichts tun.
  const darfIban = canWrite(useMe().data?.user);
  const ohneIban = useQuery({
    queryKey: ["employees", "missing-iban"],
    queryFn: () =>
      api.get<{ wartendCount: number; wartend: { id: string; name: string; personalNr: number }[] }>(
        "/employees/missing-iban",
      ),
    enabled: darfIban,
  });
  const wartend = ohneIban.data?.wartend ?? [];

  if (days.isLoading) return <Spinner label="Übersicht wird geladen" />;
  if (days.error) return <ErrorBox error={days.error} />;

  const all = days.data?.days ?? [];
  const withTips = all.filter((d) => d.tipTotal !== null && d.tipTotal !== "0.00");
  const periode = workflow.data?.periode ?? null;
  const open = all.filter((d) => d.status === "DRAFT" || d.status === "CALCULATED");
  const unbalanced = all.filter((d) => d.isBalanced === false);
  const alleBlockierenden = (blocking.data?.sessions ?? []).filter((s) => s.status !== "EXCLUDED");
  // Auf den Zeitraum eingegrenzt, wie die Kennzahlen daneben. Sonst stünde hier
  // eine andere Zahl als im Arbeitsfluss darüber, und beide meinen dasselbe.
  const blockingSessions = periode
    ? alleBlockierenden.filter(
        (s) => s.businessDate >= periode.from && s.businessDate <= periode.to,
      )
    : alleBlockierenden;
  const ausserhalb = alleBlockierenden.length - blockingSessions.length;

  return (
    <>
      <PageHeader label="Übersicht" title="Trinkgeldabrechnung">
        Kassenabschlüsse, Terminal-Reports und Stunden laufen hier zusammen. Ein Betriebstag
        wird erst freigegeben, wenn jede Kasse geklärt ist und die Kontrollsumme aufgeht.
      </PageHeader>

      {workflow.data ? <Workflow workflow={workflow.data} /> : null}

      {/*
        * Kennzahlen immer auf den Arbeitsmonat bezogen. Über den Gesamtbestand
        * gerechnet stünde hier nach der Historienmigration „135 Tage mit
        * Trinkgeld", und davon gehören 115 in den gesperrten Altbestand.
        */}
      {/* Eine neue Hälfte ohne Daten hätte hier nur Nullen. */}
      {periode && periode.tage > 0 ? (
        <div className="mb-10 flex flex-wrap gap-6">
          <Stat label="Betriebstage im Zeitraum" value={String(periode.tage)} />
          {/*
            * „offen" zählte auch schon freigegebene Tage mit — der Status wirkte
            * dadurch unzuverlässig. Jetzt genau das, was noch zu tun ist.
            */}
          <Stat
            label="noch nicht freigegeben"
            value={String(periode.entwurf + periode.berechnet)}
            tone={periode.entwurf + periode.berechnet > 0 ? "default" : "ok"}
            info={
              `${periode.entwurf} noch nicht gerechnet · ${periode.berechnet} warten auf Freigabe · ` +
              `${periode.freigegeben} freigegeben${periode.gesperrteTage > 0 ? ` · ${periode.gesperrteTage} ausgezahlt` : ""}`
            }
          />
          <Stat
            label="zu klären"
            value={String(blockingSessions.length)}
            tone={blockingSessions.length > 0 ? "alert" : "ok"}
            info="Kassen in diesem Abrechnungszeitraum, die eine Entscheidung brauchen. Solange eine offen ist, lässt sich ihr Betriebstag nicht freigeben."
          />
          <Stat
            label="Trinkgeld im Zeitraum"
            value={periode.trinkgeld ? euro(periode.trinkgeld) : "–"}
            info="Summe der bereits berechneten Tage dieses Abrechnungszeitraums. Das ist der Betrag, der ausgezahlt wird."
          />
        </div>
      ) : null}

      {wartend.length > 0 ? (
        <Section
          label="Bankdaten"
          title={wartend.length === 1 ? "Eine IBAN fehlt" : `${wartend.length} IBANs fehlen`}
          actions={
            <Link to="/stammdaten?tab=mitarbeiter&filter=wartet" className="btn-primary">
              IBAN eintragen
            </Link>
          }
        >
          <div className="card p-4 text-sm">
            <p className="mb-2">
              {wartend
                .slice(0, 6)
                .map((e) => e.name || `PersNr ${e.personalNr}`)
                .join(", ")}
              {wartend.length > 6 ? ` und ${wartend.length - 6} weitere` : ""}
            </p>
            <p className="text-muted">
              {wartend.length === 1 ? "Bekommt" : "Bekommen"} Trinkgeld, aber ohne IBAN lässt sich die Auszahlung
              nicht zur Überweisung exportieren. Meist sind das neue Namen aus dem Stundenimport.
            </p>
          </div>
        </Section>
      ) : null}

      {blockingSessions.length > 0 ? (
        <Section
          label="Zu klären"
          title="Kassensessions mit offenen Hinweisen"
          actions={
            <Link to="/kassen-review" className="btn-primary">
              Zum Review
            </Link>
          }
        >
          <div className="card overflow-hidden">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="w-36">Betriebstag</th>
                  <th>Kasse</th>
                  <th>Hinweis</th>
                </tr>
              </thead>
              <tbody>
                {blockingSessions.slice(0, 8).map((s) => (
                  <tr key={s.id}>
                    <td>
                      <Link to={`/betriebstage/${s.businessDate}`} className="hover:text-accent">
                        {longDate(s.businessDate)}
                      </Link>
                    </td>
                    <td>{s.workstationName}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {s.flags.map((f) => (
                          <FlagChip key={f} flag={f} confirmed={s.status === "CONFIRMED"} />
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {blockingSessions.length > 8 ? (
            <p className="mt-2 text-xs text-muted">
              und {blockingSessions.length - 8} weitere
            </p>
          ) : null}
          {ausserhalb > 0 ? (
            <p className="mt-2 text-xs text-muted">
              Dazu {ausserhalb} in anderen Zeiträumen. Alle stehen unter{" "}
              <Link to="/kassen-review" className="underline hover:text-ink">
                Kassen klären
              </Link>
              .
            </p>
          ) : null}
        </Section>
      ) : null}

      <Section
        label="Betriebstage"
        title="Zuletzt bearbeitet"
        actions={
          <Link to="/betriebstage" className="btn-ghost">
            Alle ansehen
          </Link>
        }
      >
        {withTips.length === 0 ? (
          <Empty>Noch keine Betriebstage mit Trinkgeld. Zuerst importieren.</Empty>
        ) : (
          <div className="card overflow-hidden">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="w-36">Tag</th>
                  <th>Veranstaltung</th>
                  <th className="w-28 text-right">Trinkgeld</th>
                  <th className="w-28">Status</th>
                </tr>
              </thead>
              <tbody>
                {withTips.slice(-10).reverse().map((d) => (
                  <tr key={d.businessDate}>
                    <td>
                      <Link to={`/betriebstage/${d.businessDate}`} className="hover:text-accent">
                        {longDate(d.businessDate)}
                      </Link>
                    </td>
                    <td className="max-w-md truncate text-muted" title={d.eventTitle ?? ""}>
                      {d.source === "LEGACY_IMPORT" ? (
                        <span
                          className="chip-muted mr-2"
                          title="Aus der alten Excel übernommen. Nur Person, Tag und Betrag, keine Kassendaten."
                        >
                          Altbestand
                        </span>
                      ) : null}
                      {d.eventTitle ?? "–"}
                    </td>
                    <td>
                      <Amount value={euro(d.tipTotal)} />
                    </td>
                    <td>
                      <StatusChip status={d.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

    </>
  );
}
